import { FormEvent, useCallback, useState } from 'react';

import * as endpoints from 'utils/api/endpoints';
import { getClearEmail } from 'utils/regex-utils';

import { isLoginRequiredError } from '../helpers/auth-errors';
import { isEmailFilled, isUsernameFilled } from '../helpers/auth-form';
import { usePublicPost } from '../hooks/use-public-post';

/** PublicApiController.PasswordResetRequest: the username only after 409 LOGIN_REQUIRED */
export type PasswordResetRequestDto = {
    email: string;
    username?: string;
};

/**
 * The form of /forgot-password: POST /public/users/reset-password. The backend answers 201 for any email (it does not
 * tell whether the account exists); 409 LOGIN_REQUIRED when several accounts share the email — then the form asks
 * for the username and sends both.
 */
export const useForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [validationMode, setValidationMode] = useState(false);
    const [body, setBody] = useState<PasswordResetRequestDto | null>(null);

    const { status, error } = usePublicPost({ url: endpoints.passwordReset, body });

    const sentWithUsername = body?.username !== undefined;
    // Only the first request (by email) can ask for the username; a 409 with the username is an ordinary error
    const loginRequired = status === 'error' && !sentWithUsername && isLoginRequiredError(error);
    const needsUsername = loginRequired || sentWithUsername;
    const submitEnabled = isEmailFilled(email) && (!needsUsername || isUsernameFilled(username));

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();

            const clearEmail = getClearEmail(email);
            if (!submitEnabled || clearEmail === null) {
                setValidationMode(true);

                return;
            }

            setBody(needsUsername ? { email: clearEmail, username: username.trim() } : { email: clearEmail });
        },
        [email, username, needsUsername, submitEnabled]
    );

    return {
        email,
        setEmail,
        username,
        setUsername,
        validationMode,
        needsUsername,
        submitEnabled,
        handleSubmit,
        pending: status === 'pending',
        success: status === 'success',
        failed: status === 'error' && !loginRequired
    };
};
